import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ShieldAlert, MapPin, Monitor, AlertTriangle, CheckCircle2 } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";

export const UnauthorizedAccessMonitor = () => {
  const { data: accessLogs, isLoading } = useQuery({
    queryKey: ["unauthorized-admin-access"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unauthorized_admin_access_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const getThreatBadge = (threatLevel: string | null) => {
    switch (threatLevel) {
      case "malicious":
        return <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />Malicious</Badge>;
      case "suspicious":
        return <Badge variant="outline" className="gap-1 text-yellow-600 border-yellow-600"><AlertTriangle className="h-3 w-3" />Suspicious</Badge>;
      case "safe":
        return <Badge variant="outline" className="gap-1 text-green-600 border-green-600"><CheckCircle2 className="h-3 w-3" />Safe</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getEnrichmentBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge variant="outline" className="text-xs">Enriched</Badge>;
      case "pending":
        return <Badge variant="secondary" className="text-xs">Pending</Badge>;
      case "failed":
        return <Badge variant="destructive" className="text-xs">Failed</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">{status}</Badge>;
    }
  };

  // Count recent attempts (last 24 hours)
  const recentAttempts = accessLogs?.filter(log => {
    const logDate = new Date(log.created_at);
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return logDate > oneDayAgo;
  }).length || 0;

  // Count high-threat attempts
  const highThreatAttempts = accessLogs?.filter(log => 
    log.threat_level === 'malicious' || log.is_tor || log.is_vpn
  ).length || 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Attempts</CardTitle>
            <ShieldAlert className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{accessLogs?.length || 0}</div>
            <p className="text-xs text-muted-foreground">All time unauthorized attempts</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last 24 Hours</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{recentAttempts}</div>
            <p className="text-xs text-muted-foreground">Recent unauthorized attempts</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Threat</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{highThreatAttempts}</div>
            <p className="text-xs text-muted-foreground">Malicious or suspicious attempts</p>
          </CardContent>
        </Card>
      </div>

      {highThreatAttempts > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Security Alert</AlertTitle>
          <AlertDescription>
            {highThreatAttempts} high-threat unauthorized access attempt{highThreatAttempts !== 1 ? 's' : ''} detected. 
            Review the logs below for potential security issues.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" />
            Unauthorized Admin Access Attempts
          </CardTitle>
          <CardDescription>
            Logs of non-admin users attempting to access admin-only pages
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : accessLogs && accessLogs.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>IP / Network</TableHead>
                    <TableHead>Threat</TableHead>
                    <TableHead>Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accessLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-sm">{log.user_email}</span>
                          <span className="text-xs text-muted-foreground font-mono">{log.user_id.slice(0, 8)}...</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded">{log.route_attempted}</code>
                      </TableCell>
                      <TableCell>
                        {log.city || log.country ? (
                          <div className="flex items-center gap-1 text-sm">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            <span>
                              {[log.city, log.region, log.country].filter(Boolean).join(", ")}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {getEnrichmentBadge(log.enrichment_status)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1 text-xs">
                            <Monitor className="h-3 w-3 text-muted-foreground" />
                            <code className="font-mono">{log.ip_address || "Unknown"}</code>
                          </div>
                          {(log.is_tor || log.is_vpn || log.proxy || log.hosting) && (
                            <div className="flex gap-1">
                              {log.is_tor && <Badge variant="destructive" className="text-xs">TOR</Badge>}
                              {log.is_vpn && <Badge variant="outline" className="text-xs">VPN</Badge>}
                              {log.proxy && <Badge variant="outline" className="text-xs">Proxy</Badge>}
                              {log.hosting && <Badge variant="outline" className="text-xs">Hosting</Badge>}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getThreatBadge(log.threat_level)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <ShieldAlert className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No unauthorized access attempts recorded</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
